import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import { MigrationOverview, MigrationRun } from 'src/types/TMigration';
import { getMigrationCopy, getMigrationOverview } from '../functions/migrationApi';
import { queryNames } from '../utils/queryNames';

/**
 * The whole migration screen's state, in one request.
 *
 * One call rather than six because the page is a state machine — not enrolled,
 * enrolled but unverified, verified but not copying, copying, live — and
 * assembling that from six independently-arriving responses makes the screen
 * flicker through wrong intermediate states on every load.
 */
export function useMigrationOverviewQuery(
  queryOptions?: UseQueryOptions<MigrationOverview, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getMigrationOverview(accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<MigrationOverview, FetchError>([queryNames.migrationOverview], queryFn, {
    // 403 (not an admin) and 503 (no migration-onramp on this deployment) are both
    // authoritative answers, not transient failures — retrying either just
    // delays the page rendering the state it already knows.
    retry: false,
    ...queryOptions,
  });
}

/**
 * One library copy, polled while it runs.
 *
 * `refetchInterval` is passed by the caller rather than fixed here: polling a
 * finished run forever is the easy mistake, and the component that knows the
 * status is the one that should decide when to stop.
 */
export function useMigrationCopyQuery(
  runId: number | undefined,
  queryOptions?: UseQueryOptions<MigrationRun, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    if (!runId) throw new Error('runId is required');
    const result = (await getMigrationCopy(runId, accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(fetchFn),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, runId],
  );

  return useQuery<MigrationRun, FetchError>([queryNames.migrationCopy, runId], queryFn, {
    enabled: !!runId,
    retry: false,
    ...queryOptions,
  });
}
